import { Component, Emit, Prop, Vue } from 'vue-property-decorator'
import { AppliedBillableConceptResponse } from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.dto'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { billingInvoicePaymentService } from '@/api/services/billing/invoice-payment/billing-invoice-payment.service'
import { store } from '@/store/store'
import { ToastUtils } from '@/shared/util/toast-utils'
import { Nullable, StrictNullable } from '@/shared/types/app.types'
import {
    InvoicePaymentResponse,
    InvoicePaymentStatus
} from '@/api/services/billing/invoice-payment/billing-invoice-payment.dto'
import { ModalUtils } from '@/shared/util/modal-utils'
import { FileUtils } from '@/shared/util/file-utils'
import { BillingOperationWithConceptsGuard } from '@/modules/billing-module/shared/guards/billing-operation-with-concepts-guard'

@Component
export default class BillingModalInvoicePaymentActionsGrid extends Vue {
    @Prop({ type: Object }) readonly invoicePayment!: Nullable<InvoicePaymentResponse>
    @Prop({ type: Array, default: () => [] }) readonly appliedBillableConcepts!: AppliedBillableConceptResponse[]

    @Emit()
    added(): void {
        // emits an event
    }

    @Emit()
    refresh(invoicePaymentId: number): number {
        return invoicePaymentId
    }

    /// COMPUTED ///

    private get _invoicePaymentGenerated(): boolean {
        return this.invoicePayment?.status.code === InvoicePaymentStatus.GENERATED
    }
    private get _invoicePaymentVerified(): boolean {
        return this.invoicePayment?.status.code === InvoicePaymentStatus.VERIFIED
    }

    get visibleOpenInvoicePaymentReport(): boolean {
        return this._invoicePaymentGenerated || this._invoicePaymentVerified
    }
    get visibleDeleteConcepts(): boolean {
        return this._invoicePaymentGenerated
    }

    private static _canRemoveConcepts(concepts: AppliedBillableConceptResponse[]): boolean {
        return !!concepts.length
    }
    get canRemoveSelectedConcepts(): boolean {
        return BillingModalInvoicePaymentActionsGrid._canRemoveConcepts(this.appliedBillableConcepts)
    }

    get canOpenInvoicePaymentReport(): boolean {
        return !!this.invoicePayment
    }

    /// SERVICES CALLS ///

    private async _deleteInvoicePaymentSelectedConcepts(conceptsIds: number[]): Promise<void> {
        if (this.invoicePayment && conceptsIds.length) {
            const { id } = this.invoicePayment
            const response =
                await PromiseUtils.toCallSecure(
                    billingInvoicePaymentService.removeConcepts(
                        store.state.labId,
                        id,
                        conceptsIds))
            if (response !== null) {
                const choice = conceptsIds.length > 1 ? 2 : 1
                ToastUtils.showToastSuccess(this.$tc('accounting.concept.deleted', choice))
                this.refresh(id) // emits an event
            }
        }
    }
    private async _deleteInvoicePaymentAllConcepts(): Promise<void> {
        if (this.invoicePayment) {
            const { id } = this.invoicePayment
            const response =
                await PromiseUtils.toCallSecure(
                    billingInvoicePaymentService.removeAllConcepts(
                        store.state.labId,
                        id))
            if (response !== null) {
                ToastUtils.showToastSuccess(this.$tc('accounting.concept.deleted', 2))
                this.refresh(id) // emits an event
            }
        }
    }

    private _downloadInvoicePaymentReport(invoicePaymentId: number): Promise<StrictNullable<Blob>> {
        return PromiseUtils.toCallSecure(
            billingInvoicePaymentService.getReport(store.getters.labId, invoicePaymentId, false),
            error => ModalUtils.showErrorModalFromErrorResponse(error, this.$tc('print.modals.errorReport')))
    }

    /// EVENT HANDLERS ///

    async onConceptsClean(): Promise<void> {
        if (this.invoicePayment) {
            const { appliedBillableConceptCount } = this.invoicePayment
            await BillingOperationWithConceptsGuard.run(
                appliedBillableConceptCount,
                this._deleteInvoicePaymentAllConcepts
            )
        }
    }
    async onConceptsDeleted(
        conceptIds = this.appliedBillableConcepts.map(abc => abc.id)
    ): Promise<void> {
        await BillingOperationWithConceptsGuard.run(
            conceptIds.length,
            () => this._deleteInvoicePaymentSelectedConcepts(conceptIds)
        )
    }

    async onOpenInvoicePaymentReport(): Promise<void> {
        if (this.invoicePayment) {
            const blob = await this._downloadInvoicePaymentReport(this.invoicePayment.id)
            if (blob !== null) {
                FileUtils.openFile({ blob })
            }
        }
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        const menuItems: MenuItemDef[] = []
        if (params.node) {
            const selectedNode: AppliedBillableConceptResponse = params.node.data
            if (BillingModalInvoicePaymentActionsGrid._canRemoveConcepts([selectedNode])) {
                menuItems.push({
                    name: this.$tc('accounting.action.deleteConcept'),
                    action: () => this.onConceptsDeleted([selectedNode.id])
                })
            }
        }
        return menuItems
    }
}
